import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
    Inject,
    Injectable,
    makeStateKey,
    Optional,
    PLATFORM_ID,
    REQUEST,
    TransferState,
} from '@angular/core';
import {
    environment as angularEnvironment,
    environment,
} from '../../../environments/environment';
import { WINDOW } from './window.service';

export enum SERVER_ENVIRONMENT {
    Production = 'Production',
    Preview = 'Preview',
    Staging = 'Staging',
    Local = 'Local',
}

@Injectable({
    providedIn: 'root',
})
export class EnvironmentService {
    static readonly serverStateEnvironmentKey = makeStateKey<string>(
        'serverStateEnvironment'
    );
    static readonly serverStateVersionKey =
        makeStateKey<string>('serverStateVersion');
    environment: SERVER_ENVIRONMENT;
    version: string;

    constructor(
        private transferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject(WINDOW) private window: Window,
        @Optional() @Inject(REQUEST) private request: Request,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.detectServerEnvironment();
    }

    getBaseLink() {
        if (this.isBrowser()) {
            return this.document.URL;
        } else {
            return this.request?.url;
        }
    }

    isProduction(): boolean {
        return (
            angularEnvironment.production &&
            !angularEnvironment.preview &&
            this.environment === SERVER_ENVIRONMENT.Production
        );
    }
    isStaging(): boolean {
        return (
            angularEnvironment.production &&
            !angularEnvironment.preview &&
            this.environment === SERVER_ENVIRONMENT.Staging
        );
    }
    isLocal(): boolean {
        return this.environment === SERVER_ENVIRONMENT.Local;
    }
    isPreview(): boolean {
        return angularEnvironment.preview;
    }

    detectServerEnvironment() {
        if (isPlatformServer(this.platformId)) {
            if (
                Object.values(SERVER_ENVIRONMENT).includes(
                    // @ts-ignore
                    process.env.ENVIRONMENT
                )
            ) {
                this.transferState.set(
                    EnvironmentService.serverStateEnvironmentKey,
                    process.env.ENVIRONMENT
                );
                this.environment = SERVER_ENVIRONMENT[process.env.ENVIRONMENT];
            } else {
                this.environment = SERVER_ENVIRONMENT.Production;
            }
            this.version = process.env.VERSION || '1.0';
            this.transferState.set(
                EnvironmentService.serverStateVersionKey,
                this.version
            );
        } else {
            if (!angularEnvironment.production) {
                this.environment = SERVER_ENVIRONMENT.Local;
                this.version = '1.0';
            } else {
                const environment = this.transferState.get<string>(
                    EnvironmentService.serverStateEnvironmentKey,
                    SERVER_ENVIRONMENT.Production
                );
                this.environment = SERVER_ENVIRONMENT[environment];

                this.version = this.transferState.get<string>(
                    EnvironmentService.serverStateVersionKey,
                    '1.0'
                );
            }
        }
    }

    isBrowser() {
        return isPlatformBrowser(this.platformId);
    }
    isServer() {
        return isPlatformServer(this.platformId);
    }

    runtimeEnvironment() {
        return this.platformId;
    }

    hostName() {
        if (this.isBrowser()) {
            return environment.host
                ? environment.host
                : this.window.location.origin;
        } else {
            // @ts-ignore
            return process.env.SERVER_HOSTNAME
                ? process.env.SERVER_HOSTNAME
                : environment.serverHost;
        }
    }
}
